import { useState } from "react";
import { createUser, deleteUser, fetchAllUsers, updateUser } from "../api/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { useUserForm } from "../hooks/useUserForm";
import UserCard from "../components/users/UserCard";
import UserSheet from "../components/users/UserSheet";

const Users = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [query, setQuery] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);

    const { form, mode, currentTelegramId, openAdd, openEdit, setField } = useUserForm();

    // Queries
    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: fetchAllUsers
    });
    const filteredUsers = users.filter((u) => `${u.name}`.toLowerCase().includes(query.toLowerCase()));

    // Handlers
    const handleAddOpen = () => {
        openAdd();
        setSheetOpen(true);
    };
    const handleEditOpen = (user) => {
        openEdit(user);
        setSheetOpen(true);
    };
    const handleSubmit = () => {
        const fn = mode === 'add'
            ? createUser({ telegram_id: parseInt(form.telegramId), name: form.name, is_admin: form.isAdmin })
            : updateUser(currentTelegramId, { new_telegram_id: parseInt(form.telegramId), new_name: form.name, is_admin: form.isAdmin });
        fn.then(() => {
            setSheetOpen(false);
            queryClient.invalidateQueries({ queryKey: ['users']});
        });
    };
    const handleDelete = (telegramId) => {
        if (!window.confirm(t('team.delete_member'))) return;
        deleteUser(telegramId).then(() => queryClient.invalidateQueries({ queryKey: ['users']}));
    };

    return (
        <div className="flex flex-col w-full max-w-md mx-auto px-3 pt-4 pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-white text-3xl font-bold">{t('team.team')}</h1>
                <button 
                onClick={handleAddOpen}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-secondary-800 text-white text-2xl font-bold">+</button>
            </div>
            
            {/* Search */}
            <input
                type="text"
                placeholder={t('team.member_search_placeholder')}
                value={query}
                onChange={(e)=> setQuery(e.target.value)}
                className="w-full bg-primary-800 text-white px-4 py-3 rounded-xl border border-primary-50 
                focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800
                placeholder:text-primary-50 text-sm mb-3"
                />
            <p className="text-primary-50 text-xs font-mono mb-3">{filteredUsers.length === 1 ? t('team.members_label_one', { count: filteredUsers.length }) : t('team.members_label_other', { count: filteredUsers.length })}</p>

            {/* Users List */}
            {isLoading ? (
                <p className="text-primary-50 text-sm text-center mt-8">{t('general.loading')}</p>
            ) : filteredUsers.length === 0 ? (
                <p className="text-primary-50 text-sm text-center mt-8">{t('team.no_members_found')}</p>
            ) : (
                <div className="flex flex-col gap-3">
                {filteredUsers.map(user => (
                    <UserCard
                    key={user.telegram_id}
                    user={user}
                    onEdit={handleEditOpen}
                    onDelete={handleDelete} />
                ))}
            </div>
            )}
            {/* Actions */}
            <UserSheet
                isOpen={sheetOpen}
                mode={mode}
                form={form}
                onFieldChange={setField}
                onClose={() => setSheetOpen(false)}
                onSubmit={handleSubmit} />
        </div>
        );
    };

export default Users;